import { Flex, Form, FormInstance } from "antd";
import { components } from "types/openapi";
import { DefaultInput, PhoneNumberInput, CustomButton, WhiteBoxBlock, DefaultRangeDateInput, MultiplySelect } from "ui";
import {
  _isRequired,
  _validateFullName,
  _isEmail,
  _endDateNotBeforeToday,
  _isRequiredAttorneyWhenDateExists,
  getAvailableUserRoles,
} from "utils";
import { USER_STATUS_ENUM, UserListEntity } from "interfaces";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";

type FieldType = components["schemas"]["CreateUserRequest"];
type CompanyRole = components["schemas"]["CompanyDto"]["role"];

interface IProps {
  form: FormInstance<any>;
  isEditPage?: boolean;
  disabled: boolean;
  initValues?: FieldType & UserListEntity;
  onFinish: () => Promise<void>;
  closeHandler: () => Promise<void>;
  companyRole?: CompanyRole;
}

export const UserForm = ({ form, initValues, onFinish, closeHandler, disabled, isEditPage, companyRole }: IProps) => {
  const availableRoles = getAvailableUserRoles(companyRole);

  return (
    <Form form={form} name="createUser" initialValues={{ ...initValues }} onFinish={onFinish}>
      <WhiteBoxBlock>
        <Flex vertical>
          <Flex gap="large">
            <Form.Item
              name="surname"
              rules={[_isRequired("Фамилия обязательна для ввода"), _validateFullName()]}
              validateDebounce={FORM_VALIDATION_DEBOUNCE}
              className="full-width"
            >
              <DefaultInput placeholder="Фамилия" disabled={disabled} />
            </Form.Item>
            <Form.Item
              name="name"
              rules={[_isRequired("Имя обязательно для ввода"), _validateFullName()]}
              validateDebounce={FORM_VALIDATION_DEBOUNCE}
              className="full-width"
            >
              <DefaultInput placeholder="Имя" disabled={disabled} />
            </Form.Item>
          </Flex>
          <Flex gap="large">
            <Form.Item
              name="secondName"
              rules={[_validateFullName()]}
              validateDebounce={FORM_VALIDATION_DEBOUNCE}
              className="full-width"
            >
              <DefaultInput placeholder="Отчество" disabled={disabled} />
            </Form.Item>
            <Form.Item
              name="position"
              rules={[_isRequired("Должность обязательна для ввода")]}
              validateDebounce={FORM_VALIDATION_DEBOUNCE}
              className="full-width"
            >
              <DefaultInput placeholder="Должность" disabled={disabled} maxLength={50} />
            </Form.Item>
          </Flex>
          <Flex gap="large">
            <PhoneNumberInput
              name="phone"
              disabled={disabled || (isEditPage && initValues?.status !== USER_STATUS_ENUM.DRAFT)}
              rules={[_isRequired("Номер телефона обязателен для ввода")]}
            />
            <Form.Item
              name="email"
              rules={[_isRequired("Email обязателен для ввода"), _isEmail()]}
              validateDebounce={FORM_VALIDATION_DEBOUNCE}
              className="full-width"
            >
              <DefaultInput
                placeholder="Email"
                disabled={disabled || (isEditPage && initValues?.status === USER_STATUS_ENUM.ACTIVE)}
              />
            </Form.Item>
          </Flex>

          <Flex gap="large">
            <Form.Item
              name="attorney"
              rules={[_isRequiredAttorneyWhenDateExists()]}
              validateDebounce={FORM_VALIDATION_DEBOUNCE}
              className="full-width"
            >
              <DefaultInput placeholder="Доверенность номер" disabled={disabled} maxLength={20} />
            </Form.Item>
            <Form.Item name="attorneyDate" className="full-width" rules={[_endDateNotBeforeToday()]}>
              <DefaultRangeDateInput allowEmpty={[false, false]} disabled={disabled} maxDate={undefined} />
            </Form.Item>
          </Flex>
          {!isEditPage && (
            <Form.Item
              name="roles"
              className="full-width"
              rules={[_isRequired("Выберите хотя бы одну роль")]}
              validateDebounce={FORM_VALIDATION_DEBOUNCE}
            >
              <MultiplySelect placeholder="Роли пользователя" disabled={disabled} options={availableRoles} />
            </Form.Item>
          )}
        </Flex>
      </WhiteBoxBlock>
      <Form.Item>
        <Flex gap="large" className="margin-top-lg">
          <CustomButton disabled={disabled} loading={disabled} htmlType="submit">
            Сохранить
          </CustomButton>
          <CustomButton
            variant="outlined"
            disabled={disabled}
            loading={disabled}
            htmlType="button"
            onClick={closeHandler}
          >
            Отмена
          </CustomButton>
        </Flex>
      </Form.Item>
    </Form>
  );
};
